import React, { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { RefreshCw, Trash2, Mail, Phone, Tag, Send, Globe } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDate, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { LeadDto, LeadSource, LeadStatus, Tone } from '@kardume/types';
import { LEAD_STATUS_LABELS } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  SearchInput,
  ActionButton,
  Badge,
  Modal,
  Button,
  ConfirmModal,
  Select,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const COLUMN_ORDER: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'NEGOTIATING', 'WON', 'LOST'];

/** Rótulo curto da origem - o card é estreito, então nada de frase. */
const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  SITE_CONTACT: 'Site',
  WHATSAPP_CAMPAIGN: 'Disparo',
};

const LEAD_SOURCE_ICONS: Record<LeadSource, React.ElementType> = {
  SITE_CONTACT: Globe,
  WHATSAPP_CAMPAIGN: Send,
};

/** Origem desconhecida (enum novo no backend, front antigo) não quebra o card. */
const sourceLabel = (source: LeadSource): string => LEAD_SOURCE_LABELS[source] ?? source;

const STATUS_SELECT_OPTIONS = COLUMN_ORDER.map((status) => ({ value: status, label: LEAD_STATUS_LABELS[status] }));

const emptyBoard = (): Record<LeadStatus, LeadDto[]> =>
  COLUMN_ORDER.reduce((acc, status) => {
    acc[status] = [];
    return acc;
  }, {} as Record<LeadStatus, LeadDto[]>);

function statusTone(status: LeadStatus): Tone {
  if (status === 'WON') return 'success';
  if (status === 'LOST') return 'danger';
  if (status === 'NEW') return 'info';
  if (status === 'NEGOTIATING') return 'warning';
  return 'secondary';
}

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Board = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  overflow-x: auto;
  padding-bottom: 0.75rem;
`;

const Column = styled.div<{ $isOver?: boolean }>`
  flex: 0 0 288px;
  width: 288px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 300px);
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  transition: box-shadow 0.15s ease;
  ${(p) => p.$isOver && `box-shadow: inset 0 0 0 2px ${p.theme.primary};`}
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${(p) => p.theme.border};

  strong {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const ColumnBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ColumnEmpty = styled.div`
  padding: 1.5rem 0.5rem;
  text-align: center;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

const LeadCard = styled.div<{ $dragging?: boolean }>`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  box-shadow: ${(p) => p.theme.shadowSm};
  padding: 0.85rem;
  cursor: grab;
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};
  transition: opacity 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.primary};
  }

  strong {
    display: block;
    font-size: 0.88rem;
    color: ${(p) => p.theme.textPrimary};
  }

  .name {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textSecondary};
    margin-bottom: 0.4rem;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.74rem;
    color: ${(p) => p.theme.textMuted};
    margin-top: 0.3rem;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.65rem;
  }

  /* Selo e data juntos à esquerda: sem este agrupamento o space-between
     empurraria a data para o meio do rodapé, entre o selo e o botão. */
  .origin {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .date {
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

/**
 * Selo de origem no rodapé do card.
 *
 * Cores distintas de propósito: um lead que preencheu o formulário do site pediu
 * para ser procurado, e um lead de disparo foi abordado a frio. Tratar os dois como
 * a mesma coisa no board leva o comercial a cobrar do segundo o interesse do
 * primeiro.
 */
const SourceTag = styled.span<{ $outbound?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${(p) => (p.$outbound ? p.theme.warning : p.theme.primary)};
  background: ${(p) => (p.$outbound ? p.theme.warning : p.theme.primary)}1a;
`;

/** Selo pronto para o card: ícone + rótulo, na cor da origem. */
const LeadSourceTag: React.FC<{ source: LeadSource }> = ({ source }) => {
  const Icon = LEAD_SOURCE_ICONS[source] ?? Globe;
  return (
    <SourceTag $outbound={source === 'WHATSAPP_CAMPAIGN'}>
      <Icon size={10} /> {sourceLabel(source)}
    </SourceTag>
  );
};

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
  margin-bottom: 1.1rem;

  div {
    font-size: 0.85rem;
  }
  strong {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${(p) => p.theme.textMuted};
    margin-bottom: 0.2rem;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0.9rem 0 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const LEAD_ID_MIME = 'application/x-kardume-lead-id';
const LEAD_STATUS_MIME = 'application/x-kardume-lead-status';

const LeadsPage: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.getLeadsBoard(), []);

  const [board, setBoard] = useState<Record<LeadStatus, LeadDto[]> | null>(null);
  const [search, setSearch] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<LeadStatus | null>(null);
  const [selected, setSelected] = useState<LeadDto | null>(null);
  const [statusDraft, setStatusDraft] = useState<LeadStatus>('NEW');
  const [notesDraft, setNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<LeadDto | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  useEffect(() => {
    if (data) setBoard({ ...emptyBoard(), ...data.columns });
  }, [data]);

  const term = search.trim().toLowerCase();

  const columnItems = (status: LeadStatus): LeadDto[] => {
    const items = board?.[status] ?? [];
    if (!term) return items;
    return items.filter((lead) =>
      [lead.name, lead.company, lead.email ?? '', lead.city ?? '', lead.segment ?? '']
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  };

  const totals = useMemo(() => {
    const all = board ? COLUMN_ORDER.flatMap((status) => board[status] ?? []) : [];
    return {
      total: all.length,
      new: (board?.NEW ?? []).length,
      won: (board?.WON ?? []).length,
      lost: (board?.LOST ?? []).length,
    };
  }, [board]);

  const isEmptyBoard = board ? COLUMN_ORDER.every((status) => (board[status] ?? []).length === 0) : true;

  const moveLead = async (leadId: string, from: LeadStatus, to: LeadStatus): Promise<void> => {
    if (from === to || !board) return;
    const lead = board[from]?.find((item) => item.id === leadId);
    if (!lead) return;

    const previousBoard = board;
    setBoard({
      ...board,
      [from]: board[from].filter((item) => item.id !== leadId),
      [to]: [{ ...lead, status: to }, ...(board[to] ?? [])],
    });

    try {
      await backofficeApi.updateLeadStatus(leadId, { status: to });
    } catch (err) {
      setBoard(previousBoard);
      showNotification(extractErrorMessage(err, 'Não foi possível mover o lead.'), 'error');
    }
  };

  const openDetails = (lead: LeadDto): void => {
    setSelected(lead);
    setStatusDraft(lead.status);
    setNotesDraft(lead.notes ?? '');
  };

  const replaceLeadInBoard = (updated: LeadDto): void => {
    setBoard((prev) => {
      if (!prev) return prev;
      const next = COLUMN_ORDER.reduce((acc, status) => {
        acc[status] = (prev[status] ?? []).filter((item) => item.id !== updated.id);
        return acc;
      }, {} as Record<LeadStatus, LeadDto[]>);
      next[updated.status] = [updated, ...(next[updated.status] ?? [])];
      return next;
    });
  };

  const saveDetails = async (): Promise<void> => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await backofficeApi.updateLeadStatus(selected.id, {
        status: statusDraft,
        notes: notesDraft.trim() || undefined,
      });
      replaceLeadInBoard(updated);
      showNotification('Lead atualizado.', 'success');
      setSelected(null);
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar as alterações.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await backofficeApi.deleteLead(deleting.id);
      setBoard((prev) => {
        if (!prev) return prev;
        return { ...prev, [deleting.status]: prev[deleting.status].filter((item) => item.id !== deleting.id) };
      });
      showNotification('Lead removido.', 'success');
      setDeleting(null);
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o lead.'), 'error');
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="Contatos recebidos pelo formulário &quot;Fale com a gente&quot; do site - arraste os cartões entre as colunas para acompanhar o funil de parceria."
        actions={
          <ActionButton title="Atualizar" onClick={reload}>
            <RefreshCw size={16} />
          </ActionButton>
        }
      />

      <StatsGrid>
        <StatCard label="Total de leads" value={formatNumber(totals.total)} />
        <StatCard label="Novos" value={formatNumber(totals.new)} iconColor={theme.info} />
        <StatCard label="Ganhos" value={formatNumber(totals.won)} iconColor={theme.success} />
        <StatCard label="Perdidos" value={formatNumber(totals.lost)} iconColor={theme.error} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome, empresa, e-mail ou cidade..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={isEmptyBoard}
        emptyTitle="Nenhum lead ainda"
        emptyDescription="Quando alguém preencher o formulário 'Fale com a gente' no site, o lead aparece aqui."
        onRetry={reload}
      >
        <Board>
          {COLUMN_ORDER.map((status) => {
            const items = columnItems(status);
            return (
              <Column
                key={status}
                $isOver={overColumn === status}
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  setOverColumn(status);
                }}
                onDragLeave={() => setOverColumn((current) => (current === status ? null : current))}
                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData(LEAD_ID_MIME);
                  const from = e.dataTransfer.getData(LEAD_STATUS_MIME) as LeadStatus;
                  setOverColumn(null);
                  setDragId(null);
                  if (leadId && from) void moveLead(leadId, from, status);
                }}
              >
                <ColumnHeader>
                  <strong>{LEAD_STATUS_LABELS[status]}</strong>
                  <Badge tone={statusTone(status)}>{items.length}</Badge>
                </ColumnHeader>
                <ColumnBody>
                  {items.length === 0 && <ColumnEmpty>Nenhum lead aqui</ColumnEmpty>}
                  {items.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      draggable
                      $dragging={dragId === lead.id}
                      onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
                        e.dataTransfer.setData(LEAD_ID_MIME, lead.id);
                        e.dataTransfer.setData(LEAD_STATUS_MIME, lead.status);
                        e.dataTransfer.effectAllowed = 'move';
                        setDragId(lead.id);
                      }}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => openDetails(lead)}
                    >
                      <strong>{lead.company}</strong>
                      <div className="name">{lead.name}</div>
                      {(lead.segment || lead.city) && (
                        <div className="meta">
                          <Tag size={12} />
                          <span>{[lead.segment, lead.city].filter(Boolean).join(' · ')}</span>
                        </div>
                      )}
                      {lead.email && (
                        <div className="meta">
                          <Mail size={12} />
                          <span>{lead.email}</span>
                        </div>
                      )}
                      <div className="meta">
                        <Phone size={12} />
                        <span>{lead.whatsapp}</span>
                      </div>
                      <div className="footer">
                        <div className="origin">
                          <LeadSourceTag source={lead.source} />
                          <span className="date">{formatDate(lead.createdAt)}</span>
                        </div>
                        <ActionButton
                          color={theme.error}
                          title="Remover lead"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(lead);
                          }}
                        >
                          <Trash2 size={14} />
                        </ActionButton>
                      </div>
                    </LeadCard>
                  ))}
                </ColumnBody>
              </Column>
            );
          })}
        </Board>
      </DataState>

      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.company}
        width="480px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSelected(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={saveDetails} isLoading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        {selected && (
          <>
            <DetailGrid>
              <div>
                <strong>Contato</strong>
                {selected.name}
              </div>
              <div>
                <strong>Segmento</strong>
                {selected.segment || '-'}
              </div>
              <div>
                <strong>E-mail</strong>
                {selected.email || '-'}
              </div>
              <div>
                <strong>WhatsApp</strong>
                {selected.whatsapp}
              </div>
              <div>
                <strong>Cidade</strong>
                {selected.city || '-'}
              </div>
              <div>
                <strong>Recebido em</strong>
                {formatDate(selected.createdAt)}
              </div>
              <div>
                <strong>Origem</strong>
                {selected.source === 'WHATSAPP_CAMPAIGN'
                  ? 'Disparo de WhatsApp'
                  : 'Formulário do site'}
              </div>
            </DetailGrid>

            <Select
              label="Status"
              options={STATUS_SELECT_OPTIONS}
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value as LeadStatus)}
            />

            <FieldLabel htmlFor="lead-notes">Anotações</FieldLabel>
            <Textarea
              id="lead-notes"
              value={notesDraft}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotesDraft(e.target.value)}
              placeholder="Registre o andamento da conversa com este lead..."
            />
          </>
        )}
      </Modal>

      <ConfirmModal
        isOpen={deleting !== null}
        title="Remover lead"
        message={
          <>
            Remover o lead de <strong>{deleting?.company}</strong>? Essa ação não pode ser desfeita.
          </>
        }
        danger
        loading={deletingBusy}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </>
  );
};

export default LeadsPage;
